import { useQuery } from '@tanstack/react-query';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/backend/lib/firebase';
import { Residence } from '@/shared/types';
import { getResidenceById } from '@/backend/services/residences';
import { isPublished } from '@/backend/lib/residenceUtils';

export const useResidences = (activeOnly = true) => {
  return useQuery({
    queryKey: ['residences', activeOnly],
    queryFn: async () => {
      const residencesRef = collection(db, 'residences');
      const q = activeOnly
        ? query(residencesRef, where('active', '==', true))
        : query(residencesRef);

      const snapshot = await getDocs(q);
      const residences = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Residence[];

      // Public callers (activeOnly) must never see drafts. Filter in memory so
      // legacy docs with no `status` field still count as published. Admin
      // callers (activeOnly === false) get everything, drafts included.
      const visible = activeOnly ? residences.filter(isPublished) : residences;

      // Sort in memory instead of in the query to avoid index requirement
      return visible.sort((a, b) => a.name.localeCompare(b.name));
    }
  });
};

/**
 * Hook to get a single residence by ID
 * @param residenceId The residence ID
 */
export const useResidence = (residenceId: string | undefined) => {
  return useQuery({
    queryKey: ['residences', residenceId],
    queryFn: () => getResidenceById(residenceId!),
    enabled: !!residenceId,
  });
};
